import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Globals } from 'app/_globals';
import { ContractorCompetencyWorkflowItem } from 'app/_models/workflow-items/contractor-competency-workflow-item';
import { WorkflowItem } from 'app/_models/workflow-items/workflow-item.model';
import { ContractorService } from 'app/_services/contractor.service';
import { NotificationService } from 'app/_services/notification.service';
import { WorkflowItemService } from 'app/_services/workflow-items.service';
import { hasWorkflowBackParams, readWorkflowBackParams } from 'app/_utils/workflow-back-link.utils';
import { environment } from 'environments/environment';

@Component({
  selector: 'contractor-certificate-approval',
  templateUrl: './contractor-certificate-approval.component.html',
})
export class ContractorCertificateApprovalComponent implements OnInit {
  private workflowItemId: any;
  private routeRole: any;
  // The inbox filters the user came in with, so every route home lands on the view they left.
  private backLinkParams: any = {};
  public title = 'Contractor Certificate Pending Approval';
  public contractorLink: any[] = [];
  public canOpenContractorLink = false;

  public saveInProgress = false;
  public tabitems: any[] = [];
  public isViewOnly = false;
  public showApprovalForm = false;

  public contractorWorkflowItem: ContractorCompetencyWorkflowItem;
  public workflowItem: ContractorCompetencyWorkflowItem;

  public rejectionForm: any;
  public showRejectionForm = false;

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private workflowItemService: WorkflowItemService,
    private notificationService: NotificationService,
    private contractorService: ContractorService,
    private globals: Globals,
  ) {
    this.routeRole = this.globals.user.routeRole;
  }

  ngOnInit() {
    const params = this.activatedRoute.snapshot.params as Params;
    const queryParams = this.activatedRoute.snapshot.queryParams as Params;

    this.workflowItemId = params.workflowId;
    this.backLinkParams = readWorkflowBackParams(queryParams);

    this.setupPage();
  }

  private setupPage() {
    // Contractor competency approvals are driven from the inbox payload when available.
    // This keeps the click-through flow aligned with the inbox cache and avoids a duplicate fetch.
    const workflowItemFromCache = this.workflowItemService.getCachedWorkflowItem(this.workflowItemId, 'CONTRACTOR_COMPETENCY');

    if (workflowItemFromCache) {
      this.setupPageWithWorkflowItem(workflowItemFromCache);
      // The cache is one-time use so the approval page always consumes the inbox payload once per click-through.
      this.workflowItemService.clearCachedWorkflowItem(this.workflowItemId, 'CONTRACTOR_COMPETENCY');
      return;
    }

    this.workflowItemService.getWorkflowItemById(this.workflowItemId).subscribe(workflowItem => {
      this.setupPageWithWorkflowItem(workflowItem);
    }, error => {
      console.log(error);
      return this.notificationService.notify('danger', 'Error fetching the data for this page', 'Please try again later');
    });
  }

  private setupPageWithWorkflowItem(workflowItem: WorkflowItem) {
    // Contractor competency keeps its own model shape and save payload.
    this.contractorWorkflowItem = workflowItem as ContractorCompetencyWorkflowItem;
    this.workflowItem = workflowItem as ContractorCompetencyWorkflowItem;
    this.setupContractorLink();
    this.setupCeritificatePage(this.contractorWorkflowItem, this.getBackLinkTabItems());
  }

  private setupCeritificatePage(workFlowItem: ContractorCompetencyWorkflowItem, tabitems: any[]) {
    if (!workFlowItem) {
      this.notificationService.notify('warning', `Cannot find this approval item`, 'Likely it has already been approved or rejected.');
      this.navigateToInbox();
      return;
    }

    this.isViewOnly = this.isClosedWorkflowItem(workFlowItem.status);
    this.title = this.isViewOnly ? 'Contractor Certificate Details' : 'Contractor Certificate Pending Approval';
    this.tabitems = tabitems;
  }

  private getBackLinkTabItems() {
    const backLinkTabItem: any = { path: '/' + this.routeRole + '/pending-items', title: 'Inbox', class: 'back-link' };

    if (hasWorkflowBackParams(this.backLinkParams)) {
      backLinkTabItem.queryParams = this.backLinkParams;
    }

    return [backLinkTabItem];
  }

  public approve() {
    if (this.isViewOnly) {
      return;
    }

    this.saveApproval();
  }

  public saveApproval() {
    if (this.isViewOnly) {
      return;
    }

    this.saveInProgress = true;
    const body = { status: 'APPROVED' };

    this.workflowItemService.approveOrRejectItem(this.workflowItemId, body, 'CONTRACTOR_COMPETENCY').subscribe(() => {
      this.saveInProgress = false;
      this.notificationService.notify('success', 'Success', 'Certificate approved');
      this.navigateToInbox();
    }, error => {
      console.error(error);
      this.notificationService.notify('danger', `Couldn't approve certificate`, `Try again later`);
      this.saveInProgress = false;
    });
  }

  public reject() {
    if (this.isViewOnly) {
      return;
    }

    this.showRejectionForm = true;
  }

  public cancelRejection() {
    if (this.isViewOnly) {
      return;
    }

    this.showRejectionForm = false;
    this.rejectionForm = '';
    this.saveInProgress = false;
  }

  public saveRejection() {
    if (this.isViewOnly) {
      return;
    }

    this.saveInProgress = true;
    const body = {
      status: 'REJECTED',
      comments: this.rejectionForm,
    };

    this.workflowItemService.approveOrRejectItem(this.workflowItemId, body, 'CONTRACTOR_COMPETENCY').subscribe(() => {
      this.saveInProgress = false;
      this.notificationService.notify('success', 'Success', 'Request rejected');
      this.navigateToInbox();
    }, error => {
      console.error(error);
      this.notificationService.notify('danger', `Couldn't save rejection`, `Try again later`);
      this.saveInProgress = false;
    });
  }

  public viewDocument(fileName) {
    this.contractorService.showFile(environment.apiUrl + 'api/ext-file/mainteanceSiteOwnerCompetencyFiles', fileName);
  }

  private navigateToInbox() {
    const navigationExtras: any = {};

    if (hasWorkflowBackParams(this.backLinkParams)) {
      navigationExtras.queryParams = this.backLinkParams;
    }

    this.router.navigate(['/' + this.routeRole + '/pending-items'], navigationExtras);
  }

  private isClosedWorkflowItem(status: string): boolean {
    return ['APPROVED', 'REJECTED', 'DOCUMENT_REJECTED'].indexOf(status) !== -1;
  }

  private setupContractorLink(): void {
    this.canOpenContractorLink = !!(this.contractorWorkflowItem && this.contractorWorkflowItem.maintenanceId)
      && this.routeRole !== 'authorising-officer';

    if (!this.canOpenContractorLink) {
      this.contractorLink = [];
      return;
    }

    this.contractorLink = ['/' + this.routeRole, 'contractors', this.contractorWorkflowItem.maintenanceId, 'certificates'];
  }

  public get showExpiryDateColumn(): boolean {
    return !!this.workflowItem
      && this.workflowItem.status === 'APPROVED'
      && !!this.workflowItem.expiryDate;
  }
}
